import { useState, useEffect } from 'react'
import { Plus, Search, Pencil, Trash2, X, Truck } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast, ConfirmModal } from '../../../components/Toast'
import { Fornitore, FornitoreForm } from '../../../types/magazzino'

const emptyForm: FornitoreForm = {
  codice: '',
  ragione_sociale: '',
  p_iva: '',
  telefono: '',
  email: '',
  via: '',
  citta: '',
  cap: '',
  provincia: '',
  note: '',
  attivo: true,
}

export default function FornitoriPage() {
  const [fornitori, setFornitori] = useState<Fornitore[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [editingFornitore, setEditingFornitore] = useState<Fornitore | null>(null)
  const [form, setForm] = useState<FornitoreForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Fornitore | null>(null)

  useEffect(() => {
    loadFornitori()
  }, [])

  const loadFornitori = async () => {
    try {
      setLoading(true)
      const res = await api.get('/v1/magazzino/fornitori')
      setFornitori(res.data)
    } catch {
      setToast({ message: 'Errore nel caricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const filteredFornitori = fornitori.filter(f => {
    if (!search) return true
    const s = search.toLowerCase()
    return (
      f.codice.toLowerCase().includes(s) ||
      f.ragione_sociale.toLowerCase().includes(s) ||
      (f.p_iva?.toLowerCase() || '').includes(s) ||
      (f.citta?.toLowerCase() || '').includes(s)
    )
  })

  const handleCreate = () => {
    setEditingFornitore(null)
    setForm(emptyForm)
    setError('')
    setShowModal(true)
  }

  const handleEdit = (fornitore: Fornitore) => {
    setEditingFornitore(fornitore)
    setForm({
      codice: fornitore.codice,
      ragione_sociale: fornitore.ragione_sociale,
      p_iva: fornitore.p_iva || '',
      telefono: fornitore.telefono || '',
      email: fornitore.email || '',
      via: fornitore.via || '',
      citta: fornitore.citta || '',
      cap: fornitore.cap || '',
      provincia: fornitore.provincia || '',
      note: fornitore.note || '',
      attivo: fornitore.attivo,
    })
    setError('')
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSaving(true)

    try {
      const data = {
        codice: form.codice.trim(),
        ragione_sociale: form.ragione_sociale.trim(),
        p_iva: form.p_iva.trim() || null,
        telefono: form.telefono.trim() || null,
        email: form.email.trim() || null,
        via: form.via.trim() || null,
        citta: form.citta.trim() || null,
        cap: form.cap.trim() || null,
        provincia: form.provincia.trim() || null,
        note: form.note.trim() || null,
        attivo: form.attivo,
      }

      if (editingFornitore) {
        await api.put(`/v1/magazzino/fornitori/${editingFornitore.id}`, data)
        setToast({ message: 'Fornitore modificato', type: 'success' })
      } else {
        await api.post('/v1/magazzino/fornitori', data)
        setToast({ message: 'Fornitore creato', type: 'success' })
      }

      setShowModal(false)
      loadFornitori()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return
    try {
      await api.delete(`/v1/magazzino/fornitori/${confirmDelete.id}`)
      setToast({ message: 'Fornitore eliminato', type: 'success' })
      loadFornitori()
    } catch (err: any) {
      setToast({ message: err.response?.data?.detail || 'Errore nella cancellazione', type: 'error' })
    } finally {
      setConfirmDelete(null)
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fornitori</h1>
          <p className="text-gray-500">Anagrafica fornitori per articoli magazzino</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="w-5 h-5" />
          Nuovo Fornitore
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca per codice, ragione sociale, P.IVA, citta'..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Tabella */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Caricamento...</div>
        ) : filteredFornitori.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Truck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Nessun fornitore trovato</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Codice</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ragione Sociale</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">P.IVA</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telefono</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Citta'</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredFornitori.map(f => (
                <tr key={f.id} className={`hover:bg-gray-50 ${!f.attivo ? 'opacity-50' : ''}`}>
                  <td className="px-6 py-4 font-mono text-sm font-medium text-gray-900">{f.codice}</td>
                  <td className="px-6 py-4 text-gray-900">{f.ragione_sociale}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{f.p_iva || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{f.telefono || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{f.email || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{f.citta || '-'}{f.provincia ? ` (${f.provincia})` : ''}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      f.attivo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {f.attivo ? 'Attivo' : 'Disattivato'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => handleEdit(f)} className="p-2 text-gray-400 hover:text-primary-600" title="Modifica">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => setConfirmDelete(f)} className="p-2 text-gray-400 hover:text-red-600" title="Elimina">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal Crea/Modifica */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingFornitore ? 'Modifica Fornitore' : 'Nuovo Fornitore'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Codice *</label>
                  <input
                    type="text"
                    required
                    value={form.codice}
                    onChange={(e) => setForm({ ...form, codice: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="FOR-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">P.IVA</label>
                  <input
                    type="text"
                    value={form.p_iva}
                    onChange={(e) => setForm({ ...form, p_iva: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="01234567890"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ragione Sociale *</label>
                <input
                  type="text"
                  required
                  value={form.ragione_sociale}
                  onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Nome azienda fornitore"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                  <input
                    type="text"
                    value={form.telefono}
                    onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo</label>
                <input
                  type="text"
                  value={form.via}
                  onChange={(e) => setForm({ ...form, via: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Via/Piazza..."
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Citta'</label>
                  <input
                    type="text"
                    value={form.citta}
                    onChange={(e) => setForm({ ...form, citta: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CAP</label>
                  <input
                    type="text"
                    value={form.cap}
                    onChange={(e) => setForm({ ...form, cap: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    maxLength={5}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prov.</label>
                  <input
                    type="text"
                    value={form.provincia}
                    onChange={(e) => setForm({ ...form, provincia: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    maxLength={2}
                    placeholder="TO"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {editingFornitore && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="attivo"
                    checked={form.attivo}
                    onChange={(e) => setForm({ ...form, attivo: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="attivo" className="text-sm text-gray-700">Attivo</label>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Confirm Delete */}
      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Elimina Fornitore"
        message={`Sei sicuro di voler eliminare il fornitore "${confirmDelete?.codice} - ${confirmDelete?.ragione_sociale}"?`}
        confirmText="Elimina"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  )
}
